
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useReferralSystem } from "@/hooks/use-referral-system";
import { Link, Copy, Plus } from "lucide-react";
import { toast } from "sonner";

export const ReferralCodeGenerator = () => {
  const { referralCode, isLoadingCode, createReferralCode } = useReferralSystem();

  const copyReferralLink = () => {
    if (referralCode) {
      const link = `${window.location.origin}/?ref=${referralCode.code}`;
      navigator.clipboard.writeText(link);
      toast.success('Referral link copied to clipboard!');
    }
  };

  const copyReferralCode = () => {
    if (referralCode) {
      navigator.clipboard.writeText(referralCode.code);
      toast.success('Referral code copied to clipboard!');
    }
  };

  if (isLoadingCode) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Your Referral Code</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-4">
            <div className="h-4 bg-gray-200 rounded w-1/4"></div>
            <div className="h-10 bg-gray-200 rounded"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Link className="h-5 w-5" />
            Your Referral Code
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {referralCode ? (
            <>
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <div className="text-sm text-gray-600">Referral Code</div>
                  <div className="text-xl font-mono font-bold">{referralCode.code}</div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant={referralCode.is_active ? "default" : "secondary"}>
                    {referralCode.is_active ? "Active" : "Inactive"}
                  </Badge>
                  <Button onClick={copyReferralCode} variant="outline" size="sm">
                    <Copy className="h-4 w-4 mr-2" />
                    Copy Code
                  </Button>
                </div>
              </div>

              <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg">
                <div>
                  <div className="text-sm text-blue-600">Full Referral Link</div>
                  <code className="text-sm break-all">
                    {window.location.origin}/?ref={referralCode.code}
                  </code>
                </div>
                <Button onClick={copyReferralLink} variant="outline" size="sm">
                  <Copy className="h-4 w-4 mr-2" />
                  Copy Link
                </Button>
              </div>

              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <h3 className="font-semibold text-green-800 mb-2">How it works:</h3>
                <ul className="text-sm text-green-700 space-y-1">
                  <li>• Share your referral link with friends and colleagues</li>
                  <li>• When they sign up and purchase a subscription, you earn a commission</li>
                  <li>• Commissions are paid monthly via USDC or PayPal</li>
                  <li>• Top monthly referrer gets a bonus reward!</li>
                </ul>
              </div>
            </>
          ) : (
            <div className="text-center py-8">
              <div className="mb-4">
                <Link className="h-12 w-12 mx-auto text-gray-400" />
              </div>
              <h3 className="text-lg font-semibold mb-2">No referral code yet</h3>
              <p className="text-gray-600 mb-4">
                Create your unique referral code to start earning commissions
              </p>
              <Button 
                onClick={() => createReferralCode.mutate()} 
                disabled={createReferralCode.isPending}
              >
                <Plus className="h-4 w-4 mr-2" />
                {createReferralCode.isPending ? "Creating..." : "Create Referral Code"}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {referralCode && (
        <Card>
          <CardHeader>
            <CardTitle>Marketing Materials</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <h4 className="font-semibold mb-2">Sample Social Media Post</h4>
              <div className="p-4 bg-gray-50 rounded-lg text-sm">
                <p>🚀 Transform your business strategy with CSO Agent - the AI-powered platform that creates professional business documents in minutes!</p>
                <p className="mt-2">✅ Strategic plans, pitch decks, financial forecasts & more</p>
                <p>✅ Save weeks of work with AI assistance</p>
                <p>✅ Professional results every time</p>
                <p className="mt-2">Try it now: {window.location.origin}/?ref={referralCode.code}</p>
              </div>
            </div>

            <div>
              <h4 className="font-semibold mb-2">Email Template</h4>
              <div className="p-4 bg-gray-50 rounded-lg text-sm">
                <p><strong>Subject:</strong> This AI tool will revolutionize your business planning</p>
                <p className="mt-2">Hi [Name],</p>
                <p className="mt-2">I've been using CSO Agent and it's incredible how it can generate professional business documents in minutes. It's saved me countless hours on strategic planning, pitch decks, and financial forecasts.</p>
                <p className="mt-2">I thought you might find it useful for your business too. Check it out here: {window.location.origin}/?ref={referralCode.code}</p>
                <p className="mt-2">Best regards,<br/>[Your Name]</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
